import { LogsEvent, LogsInitConfiguration } from '@datadog/browser-logs';
import { DefaultPrivacyLevel, RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import { ConvivaConfig, DatadogConfig, ModelTiersConfig, ModelYearsConfig } from 'store/config';
import { getEnvFromHost } from 'utils/env';
import { getDeviceMetadata, getDeviceModelTier, getDeviceModelYear } from 'utils/platform';

let initialized = false;

enum Origin {
  CONSOLE = 'console',
  NETWORK = 'network',
}

enum Status {
  ERROR = 'error',
}

export const initializeDatadog = async (
  datadogConfig: DatadogConfig,
  convivaConfig: ConvivaConfig,
  modelTierConfig: ModelTiersConfig,
  modelYearsConfig: ModelYearsConfig,
) => {
  if (!initialized) {
    initialized = true;

    const env = getEnvFromHost();
    const version = __VERSION__;

    const { botPattern, logs, rum } = datadogConfig;

    const { defaultPrivacyLevel: dpl, enabled: rumEnabled, team: rumTeam, ...rumConfig } = rum;
    const defaultPrivacyLevel = dpl as DefaultPrivacyLevel;

    const regex = new RegExp(botPattern, 'i');
    const shouldFilterBots = regex.test(navigator.userAgent);

    const deviceMetadata = await getDeviceMetadata(convivaConfig.deviceMetadata);
    const modelYear = await getDeviceModelYear(modelYearsConfig);
    const modelTier = await getDeviceModelTier(modelTierConfig);

    const excludedUrlRegexes = (exludedUrls: string[] = []) =>
      exludedUrls
        .map((pattern) => {
          try {
            return new RegExp(pattern, 'i');
          } catch {
            return null;
          }
        })
        .filter(Boolean) as RegExp[];

    if (rumEnabled) {
      const { datadogRum } = await import('@datadog/browser-rum');
      const excludedErrors = (rumConfig?.filters?.excludedErrors ?? []).map(
        (filter: { type?: string; messagePattern?: string }) => ({
          messageRegex: filter.messagePattern ? new RegExp(filter.messagePattern, 'i') : null,
          type: filter.type,
        }),
      );
      datadogRum.setGlobalContext({
        configShortSha: rumConfig.configShortSha,
        configVersion: rumConfig.configVersion,
        model: deviceMetadata.DeviceModel,
        modelTier,
        modelYear,
        platform: deviceMetadata.DeviceManufacturer,
        team: rumTeam,
      });
      datadogRum.init({
        ...rumConfig,
        beforeSend: (event: RumEvent) => {
          // Drop error events matching configured exclusion filters.
          // Used to suppress non-actionable errors like cross-origin SecurityErrors
          // from third-party SDKs (e.g., Google IMA DAI on Samsung Tizen).
          if (event?.type === 'error' && excludedErrors.length > 0) {
            const errorType = event?.error?.type ?? '';
            const errorMessage = event?.error?.message ?? '';
            const isExcluded = excludedErrors.some(
              (filter: { type?: string; messageRegex: RegExp | null }) => {
                const typeMatch = !filter.type || filter.type === errorType;
                const messageMatch = !filter.messageRegex || filter.messageRegex.test(errorMessage);
                return typeMatch && messageMatch;
              },
            );
            if (isExcluded) {
              return false;
            }
          }

          return true;
        },
        defaultPrivacyLevel,
        env,
        sessionSampleRate: shouldFilterBots ? 0 : rumConfig.sessionSampleRate,
        version,
      } as RumInitConfiguration);
    }

    const { enabled: logsEnabled, team: logsTeam, ...logsConfig } = logs;

    const checkLogFilters = (log: LogsEvent): boolean => {
      const filters = datadogConfig.logs.filters;
      const excludedLogsUrlRegexes = excludedUrlRegexes(filters?.excludedURLRegEx);
      if (!filters) return true;

      // if configured, exclude console messages with a status of "error"
      if (
        filters.excludeConsoleErrors &&
        log?.origin === Origin.CONSOLE &&
        log?.status === Status.ERROR
      ) {
        return false;
      }

      const message = log?.message ?? '';

      // exclude any logs that include excluded message language
      if (filters.excludeMessages?.some((excluded) => message.includes(excluded))) {
        return false;
      }

      // exclude network traffic based on URL regex (with status of error)
      const urlCheck = log?.http?.url ?? '';
      if (
        log?.origin === Origin.NETWORK &&
        log?.status === Status.ERROR &&
        excludedLogsUrlRegexes.some((excludedRegex) => excludedRegex.test(urlCheck))
      ) {
        return false;
      }

      return true;
    };

    if (logsEnabled) {
      const { datadogLogs } = await import('@datadog/browser-logs');
      datadogLogs.init({
        beforeSend: checkLogFilters,
        ...logsConfig,
        env,
        sessionSampleRate: shouldFilterBots ? 0 : logsConfig.sessionSampleRate,
        version,
      } as LogsInitConfiguration);
      datadogLogs.setGlobalContext({
        configShortSha: logsConfig.configShortSha,
        configVersion: logsConfig.configVersion,
        model: deviceMetadata.DeviceModel,
        modelTier,
        modelYear,
        platform: deviceMetadata.DeviceManufacturer,
        team: logsTeam,
      });
    }
  }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const stripFunctionsFromValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item !== 'function').map(stripFunctionsFromValue);
  }
  if (isPlainObject(value)) {
    return stripFunctionsFromObject(value);
  }
  return value;
};

export const stripFunctionsFromObject = (
  obj: Record<string, unknown> = {},
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => typeof value !== 'function')
      .map(([key, value]) => [key, stripFunctionsFromValue(value)]),
  );
